/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { Mesh, Object3D, PlaneGeometry, ShaderMaterial, Vector2 } from 'three';

import Loaders from '../../../../utils/Loaders';
import PerformanceProfile from '../../../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

const CONFIG = {
    alpha: 0.75,
    ratio: 890 / 1080,
    column: 4,
    row: 3,
    frames: 12,
    frameRate: 13,
    height: 2.12,
    position: new Vector2(0.07, -0.01),
    rotation: 0.2,
    urlSD: '/assets/webgl/textures/cuteffect-spritesheet.png',
    urlHD: '/assets/webgl/textures/cuteffect-spritesheet-hq.png',
    textureSD: null,
    textureHD: null,
};

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.floorGameDown.addFolder({
        title: `CutEffect`,
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('CutEffect', CONFIG);
    });

    GUI.addInput(CONFIG, 'alpha', { min: 0, max: 1.0, step: 0.001 });
    GUI.addInput(CONFIG, 'height', { min: 0, max: 3, step: 0.001 });
    GUI.addInput(CONFIG, 'frameRate', { min: 0, max: 60, step: 1 });
    GUI.addInput(CONFIG, 'position', {
        x: { min: -5, max: 5, step: 0.01 },
        y: { min: -5, max: 5, step: 0.01 },
    });
    GUI.addInput(CONFIG, 'rotation', { min: -Math.PI * 0.5, max: Math.PI * 0.5, step: 0.01 });
};

if (PerformanceProfile.devMode) addGUI();

interface CutEffectOptions {
    isDetailView?: boolean;
}

export default class CutEffect extends Object3D {
    mesh: Mesh;
    visible = false;

    constructor(options?: CutEffectOptions) {
        super();

        const geometry = new PlaneGeometry(1.0, 1.0);
        const material = new ShaderMaterial({
            uniforms: {
                uTexture: { value: null },
                uSpriteSize: { value: new Vector2(1.0, 1.0) },
                uSpriteIndex: { value: new Vector2(0.0, 0.0) },
                uTransition: { value: 0.0 },
                uAlpha: { value: CONFIG.alpha },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
        });

        this.mesh = new Mesh(geometry, material);
        this.add(this.mesh);

        this.mesh.scale.set(CONFIG.height * CONFIG.ratio, CONFIG.height, 1.0);

        this.mesh.material.uniforms.uSpriteSize.value.x = 1.0 / CONFIG.column;
        this.mesh.material.uniforms.uSpriteSize.value.y = 1.0 / CONFIG.row;

        this.rotation.z = CONFIG.rotation;
        this.position.x = CONFIG.position.x;
        this.position.y = CONFIG.position.y;

        this.loadTexture(options?.isDetailView ?? false);

        if (PerformanceProfile.devMode) GUI.on('change', this.updateUniforms.bind(this));
    }

    loadTexture(isDetailView?: boolean) {
        if (isDetailView) {
            if (CONFIG.textureHD) {
                this.mesh.material.uniforms.uTexture.value = CONFIG.textureHD;
                this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
            } else {
                Loaders.textureLoader.load(CONFIG.urlHD, (texture) => {
                    CONFIG.textureHD = texture;
                    this.mesh.material.uniforms.uTexture.value = texture;
                    this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
                });
            }
        } else {
            if (CONFIG.textureSD) {
                this.mesh.material.uniforms.uTexture.value = CONFIG.textureSD;
                this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
            } else {
                Loaders.textureLoader.load(CONFIG.urlSD, (texture) => {
                    CONFIG.textureSD = texture;
                    this.mesh.material.uniforms.uTexture.value = texture;
                    this.mesh.material.uniforms.uTexture.value.needsUpdate = true;
                });
            }
        }
    }

    animateIn(delay: number) {
        setTimeout(() => {
            this.playSprite();

            setTimeout(
                () => {
                    this.visible = false;
                },
                (1000 / CONFIG.frameRate) * (CONFIG.frames + 1.0),
            );
        }, delay * 1000);
    }

    playSprite() {
        this.visible = true;

        let idX = 0.0;
        let idY = 0.0;

        // this.mesh.material.uniforms.uSpriteIndex.value.x = 0 / CONFIG.column;
        // this.mesh.material.uniforms.uSpriteIndex.value.y = 2 / CONFIG.row;

        for (let i = 0; i < CONFIG.frames; i++) {
            idX = i % CONFIG.column;

            const x = idX / CONFIG.column;
            const y = idY / CONFIG.row;

            setTimeout(
                () => {
                    this.mesh.material.uniforms.uSpriteIndex.value.x = x;
                    this.mesh.material.uniforms.uSpriteIndex.value.y = y;
                },
                (1000 / CONFIG.frameRate) * i,
            );

            if (idX === CONFIG.column - 1) {
                idY += 1.0;
            }

            idX += 1.0;
        }
    }

    updateUniforms() {
        this.mesh.material.uniforms.uAlpha.value = CONFIG.alpha;
        this.mesh.scale.set(CONFIG.height * CONFIG.ratio, CONFIG.height, 1.0);
        this.rotation.z = CONFIG.rotation;

        this.position.x = CONFIG.position.x;
        this.position.y = CONFIG.position.y;
    }

    update() {
        if (!this.visible) return;
    }

    delete() {
        if (this.mesh.material.uniforms.uTexture.value) {
            this.mesh.material.uniforms.uTexture.value.dispose();
        }

        this.mesh.material.dispose();
        this.mesh.geometry.dispose();
    }
}
